import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, ExternalLinkIcon, XIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { SiteFooter } from "../components/SiteFooter";
import { ListingDirectory } from "../components/ListingDirectory";
import { type CurrentListing } from "../data/currentListings";
const currentListingsVideoUrl = "https://cdn.magicpatterns.com/uploads/obHvbRbEzXUVTAeGmq1jnr/lv_0_20260919114149.mp4";
const currentListingsPhoneUrl = "https://cdn.magicpatterns.com/uploads/fS74TxB2rQzC3X3Xmvtsd6/file_00000000941481f7b0c6260a64ad6401.png";
const mobileCurrentListingsVideoUrl = "https://cdn.magicpatterns.com/uploads/xe8U2ksmxoj4DopbroxttL/lv_0_20260919120317.mp4";
export function CurrentListings() {
  const [showPhone, setShowPhone] = useState(false);
  const [mobileVideoComplete, setMobileVideoComplete] = useState(false);
  const [selectedListing, setSelectedListing] = useState<CurrentListing | null>(null);
  const prefersReducedMotion = useReducedMotion();
  return <main className="min-h-screen w-full bg-amber-950 text-amber-50" data-id="element-94" data-mp="2q9ef5a0auabe">
      <section className="relative hidden min-h-screen overflow-hidden sm:block" data-id="element-95" data-mp="2qbqudfeoa6kr">
        <Link to="/mls-listings" className="absolute left-8 top-8 z-30 inline-flex items-center gap-2 rounded-md border border-white/50 bg-stone-950/80 px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-[background-color] duration-200 hover:bg-stone-950" data-id="element-96" data-mp="2v9sabrgsxps0">
          <ArrowLeftIcon size={17} aria-hidden="true" data-id="element-97" data-mp="27jpmjnjx75z7" />
          Back to Available Listings
        </Link>

        {!showPhone ? <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted playsInline preload="auto" onEnded={() => setShowPhone(true)} onError={() => setShowPhone(true)} aria-label="Teri's current listings introduction" data-id="element-98" data-mp="2h8zft6qi2uow">
            <source src={currentListingsVideoUrl} type="video/mp4" data-id="element-99" data-mp="1gqxvgucric69" />
          </video> : <motion.div className="absolute inset-0" initial={prefersReducedMotion ? false : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-100" data-mp="1bse9wv22z2ef">
            <img src={currentListingsPhoneUrl} alt="Teri holding a phone with current listings" className="h-full w-full object-cover object-center" data-id="element-101" data-mp="xtehdcbg63cz" />
            <div className="absolute inset-0 bg-stone-950/15" aria-hidden="true" data-id="element-102" data-mp="17rav8bvrrces" />
            <div className="phone-listings-panel absolute" aria-label="Teri's property directory" data-id="element-103" data-mp="17p7wn3gjwayg">
              <ListingDirectory onSelectListing={setSelectedListing} data-id="element-104" data-mp="12uu2t5hjebvw" />
            </div>
          </motion.div>}
      </section>

      <section className="relative min-h-screen overflow-hidden sm:hidden" data-id="element-105" data-mp="2e8frkpjhibgw">
        <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted playsInline preload="auto" onEnded={event => {
        event.currentTarget.pause();
        setMobileVideoComplete(true);
      }} onError={() => setMobileVideoComplete(true)} aria-label="Teri's current listings introduction" data-id="element-106" data-mp="2lrfc4yiom91u">
          <source src={mobileCurrentListingsVideoUrl} type="video/mp4" data-id="element-107" data-mp="34hdi992x5s2u" />
        </video>
        <div className="absolute inset-0 bg-stone-950/15" aria-hidden="true" data-id="element-108" data-mp="39h9nfbncrpjz" />
        <Link to="/mls-listings" className="absolute left-5 top-5 z-20 inline-flex items-center gap-2 rounded-md border border-white/50 bg-stone-950/80 px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-[background-color] duration-200 hover:bg-stone-950" data-id="element-109" data-mp="2vpbkxjo5crws">
          <ArrowLeftIcon size={17} aria-hidden="true" data-id="element-110" data-mp="3neok91fxqzlz" />
          Back to Available Listings
        </Link>
        <AnimatePresence data-id="element-111" data-mp="24iwkbp1ulm87">
          {mobileVideoComplete && <motion.div className="mobile-phone-listings-panel absolute z-10 grid gap-3" initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.22, 1, 0.36, 1]
        }} aria-label="Teri's current listings" data-id="element-112" data-mp="24k7m5jbqgjwo">
              <ListingDirectory onSelectListing={setSelectedListing} data-id="element-113" data-mp="2n9npeeesyqgs" />
            </motion.div>}
        </AnimatePresence>
      </section>

      <SiteFooter tone="warm" data-id="element-114" data-mp="2ice1j372amv2" />

      <AnimatePresence data-id="element-115" data-mp="2djchkgrzzx4m">
        {selectedListing && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/80 p-6" initial={prefersReducedMotion ? false : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.22, 1, 0.36, 1]
      }} role="dialog" aria-modal="true" aria-labelledby="listing-preview-title" data-id="element-116" data-mp="2dhrs35kjj4dm">
            <motion.div className="relative flex h-[min(82vh,52rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-amber-100/50 bg-stone-950 shadow-2xl" initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 12,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 8,
          scale: 0.98
        }} transition={{
          duration: 0.22,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-117" data-mp="sp40hs3a6r7m">
              <header className="flex items-center justify-between gap-6 border-b border-white/15 bg-stone-950 px-6 py-4 text-white" data-id="element-118" data-mp="1gf6nvuw938zw">
                <div data-id="element-119" data-mp="1kh1rj61nlygu">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-200" data-id="element-120" data-mp="1kga4wkq1bb2p">Virtual property tour</p>
                  <h2 id="listing-preview-title" className="mt-1 font-display text-2xl leading-none" data-id="element-121" data-mp="1pds43je92bxi">{selectedListing.address}</h2>
                </div>
                <div className="flex items-center gap-2" data-id="element-122" data-mp="3qhcjekj9pi3r">
                  <button type="button" onClick={() => setSelectedListing(null)} className="rounded-md p-2 text-white transition-[background-color] duration-150 hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100" aria-label="Close listing preview" data-id="element-123" data-mp="3qfrlckxqvh15">
                    <XIcon size={20} aria-hidden="true" data-id="element-124" data-mp="d5aqh024irj8" />
                  </button>
                </div>
              </header>
              <iframe src={selectedListing.virtualTourUrl} title={`Virtual tour for ${selectedListing.address}`} className="min-h-0 flex-1 border-0 bg-white" allow="fullscreen; autoplay" data-id="element-125" data-mp="n1du6acmiy8a" />
              <footer className="flex items-center justify-between gap-4 border-t border-white/15 bg-stone-950 px-6 py-3 text-sm text-white/75" data-id="element-126" data-mp="2ot0lye4cax5t">
                <span data-id="element-127" data-mp="zb3ftkd7sci4">For complete listing details, pricing, and availability:</span>
                <a href={selectedListing.fullListingUrl} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-2 rounded-md bg-amber-200 px-3 py-2 text-xs font-bold text-stone-950 transition-[background-color] duration-150 hover:bg-amber-100" data-id="element-128" data-mp="14agcewtw0xxw">
                  View full listing <ExternalLinkIcon size={15} aria-hidden="true" data-id="element-129" data-mp="gnhyo6x5k43o" />
                </a>
              </footer>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </main>;
}